import { EventTrackerService } from 'src/app/common/_services/eventTracker.service';
import {
  Component,
  HostListener,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AsyncPipe, DOCUMENT } from '@angular/common';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { ThemeConfig } from '../assets/data/enum/themeConfig';
import { TakeLanguageOfBrowserService } from './common/_services/takeLanguageOfBrowser.service';
import { GoogleTagManagerService } from './common/_services/google/angular-google-tag-manager.service';
import { environment } from '../environments/environment';
import { ThemeService } from './common/_theme/theme.service';
import { User } from './common/_models';
import { SUPPORTED_LANGUAGES } from './config/locale.config';
import { LocaleService } from './common/_services/locale.service';
import { DownloadAppOverlayModule } from './shared/download-app-overlay/download-app-overlay.module';
import { XInformationBarModule } from './shared/x-information-bar/x-information-bar.module';
import { CookieBannerComponent } from './cookie-banner/cookie-banner.component';
import { isLocalhost } from '../main';
import { setRoutingLoaderService } from './common/_factory/routing-loader.factory';
import { RoutingUtilitiesService } from './common/_services/routingUtlities.service';
import { SplashScreenPageComponent } from './shared/splash-screen-page/splash-screen-page.component';
import { GeneralUtilsService } from './common/_services';
import { PosthogService } from './common/_services/posthog.service';
import { XChatSupportComponent } from './shared/x-chat-support/x-chat-support.component';

declare let gtag: Function;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    RouterOutlet,
    XInformationBarModule,
    DownloadAppOverlayModule,
    CookieBannerComponent,
    SplashScreenPageComponent,
    AsyncPipe,
    XChatSupportComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  public router = inject(Router);
  private translate = inject(TranslateService);
  private takeLanguageOfBrowserService = inject(TakeLanguageOfBrowserService);
  private gtmService = inject(GoogleTagManagerService);
  private themeService = inject(ThemeService);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  private localeService = inject(LocaleService);
  private routingUtilitiesService = inject(RoutingUtilitiesService);
  private generalUtilsService = inject(GeneralUtilsService);
  private eventTrackerService = inject(EventTrackerService);
  private ngZone = inject(NgZone);

  private subscriptions: Subscription = new Subscription();
  themeConfig = ThemeConfig;
  isMobile = false;
  user: User = new User();
  scriptsTranslateLoaded = false;
  environment = environment;
  showSplashScreenComponent$ = this.generalUtilsService.showSplashScreenComponent$;
  publicMode: boolean;

  constructor(_: PosthogService) {
    this.themeService.initTheme();
  }

  /**
   * On tab close listener.
   */
  @HostListener('window:beforeunload')
  onTabClose() {
    this.eventTrackerService.registerTabCloseEvent();
  }

  /**
   * On window blur listener.
   * User remove focus from the windows.
   * (Change tab, minimize, etc.)
   */
  @HostListener('window:blur')
  onBlur() {
    this.eventTrackerService.pauseEventTracking();
  }

  /**
   * On window focus listener.
   * User focus on the windows.
   * (Come back to the tab, maximize, etc.)
   */
  @HostListener('window:focus')
  onFocus() {
    this.eventTrackerService.resumeEventTracking();
  }

  ngOnInit() {
    // External scripts outside Angular
    this.ngZone.runOutsideAngular(() => {
      this.setGoogleTagManager();
      this.setGoogleAnalytics();
      this.loadTranslateScripts();
    });

    this.startRouteTrackToGoogleAnalytics();
    setRoutingLoaderService(this.routingUtilitiesService);

    // Set version from environment
    this.themeService.getVersion().subscribe();

    // Disable console log in environment.production
    if (environment.production && !isLocalhost) {
      window.console.log = () => {};
      window.console.warn = () => {};
    }

    // Set the theme colors
    this.themeService.setThemeBySaas(this.themeService.getThemeBySaas().theme);

    // Set title tag
    this.themeService.setTitleTagHeader();

    // Set icon metadata in header
    this.themeService.setLinkRelsInHeader();

    // Set header meta tags
    this.themeService.setMetaTAGsInHeader();

    // Setting ng-translate available languages
    const languages = [...SUPPORTED_LANGUAGES];
    if (environment.enableTranslateMode) {
      languages.push('ach');
    }
    this.translate.addLangs(languages);

    // Setting the default language from the user browser
    const defaultLang = this.takeLanguageOfBrowserService.getLanguage();
    this.translate.setDefaultLang(defaultLang);

    // Search for a language stored in the local storage
    const storedLng = localStorage.getItem('crntLng');
    const langToUse =
      storedLng && SUPPORTED_LANGUAGES.includes(storedLng) ? storedLng : defaultLang;

    this.translate.use(langToUse);
    this.localeService.setLocale(langToUse);
    localStorage.setItem('crntLng', langToUse);

    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          map((e) => e.urlAfterRedirects ?? e.url),
          distinctUntilChanged()
        )
        .subscribe((url) => {
          this.publicMode = !url.includes('auth');
        })
    );
  }

  setGoogleTagManager() {
    const script = this.renderer.createElement('script');
    this.renderer.setAttribute(
      script,
      'src',
      `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsId}`
    );
    this.renderer.setAttribute(script, 'async', 'true');
    this.renderer.appendChild(this.document.head, script);
  }

  setGoogleAnalytics() {
    // Creation of script tag with variable
    const customScript = this.renderer.createElement('script');
    customScript.text = `
      window.dataLayer = window.dataLayer || []
      function gtag(){dataLayer.push(arguments)}
      gtag('js', new Date())
      gtag('config', '${environment.googleAnalyticsId}')
    `;
    // Add script into head
    this.renderer.appendChild(this.document.head, customScript);
  }

  startRouteTrackToGoogleAnalytics() {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          map((e) => e.urlAfterRedirects ?? e.url),
          distinctUntilChanged()
        )
        .subscribe((url) => {
          this.ngZone.runOutsideAngular(() => {
            this.gtmService.pushTag({ event: 'page', pageName: url });
            gtag('config', environment.googleAnalyticsId, { page_path: url });
          });
        })
    );
  }

  loadTranslateScripts() {
    if (!environment.enableTranslateMode || this.scriptsTranslateLoaded) return;

    this.scriptsTranslateLoaded = true;

    // First Crowdin script
    const script1 = document.createElement('script');
    script1.type = 'text/javascript';
    script1.text = `
      var _jipt = [];
      _jipt.push(['project', 'c08b38a95009becbd0d096db525d11ac']);
      _jipt.push(['domain', 'xfarm']);
    `;
    document.head.appendChild(script1);

    // Second Crowdin script
    const script2 = document.createElement('script');
    script2.type = 'text/javascript';
    script2.text = `
      var _jipt = [];
      _jipt.push(['project', 'c08b38a95009becbd0d096db525d11ac']);
      _jipt.push(['domain', 'xfarm']);
      _jipt.push(['preload_texts', false]);
      _jipt.push(['edit_strings_context', function(context) {
        var separator = "\\n";
        var lineStart = "* ";
        var contextParts = context.split(separator);
        var linkCount = 0;

        for (var i in contextParts) {
          var line = contextParts[i];
          if (lineStart + window.location.href === line) {
            return context;
          }

          if (line.indexOf(lineStart) === 0) {
            linkCount++;
          }

          if (line === 'And more...') {
            return context;
          }
        }

        if (linkCount < 5) {
          return context + separator + lineStart + window.location.href;
        }

        return context + separator + 'And more...';
      }]);
    `;
    document.head.appendChild(script2);

    // Script with external src Crowdin
    const script3 = document.createElement('script');
    script3.type = 'text/javascript';
    script3.src = '//cdn.crowdin.com/jipt/jipt.js';
    document.head.appendChild(script3);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
